import { Model, Factory, belongsTo, trait } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  rand,
  randPastDate,
  randRecentDate,
  randNumber,
  randChanceBoolean,
  randProductName,
  randAbbreviation,
  randWord,
} from '@ngneat/falso'
import type { MockServer, MirageServer } from './../server.js'
import { pageable, filterable, getExistingEntity } from './../utils.js'
import type { ConsultationLaboratoryAnalysisAttributes } from '@/composables/consultationLaboratoryAnalyses.js'

export const model = (
  Model as ModelDefinition<ConsultationLaboratoryAnalysisAttributes>
).extend({
  consultationLaboratoryReferral: belongsTo(),
  createdUser: belongsTo('user'),
  modifiedUser: belongsTo('user'),
})

export const factory = Factory.extend<ConsultationLaboratoryAnalysisAttributes>(
  {
    code() {
      return randAbbreviation().toUpperCase()
    },
    name() {
      return `${randWord({ capitalize: true })} ${randWord({ capitalize: true })}`
    },
    quantity() {
      return randNumber({ min: 1, max: 5, precision: 0 })
    },
    used() {
      return randChanceBoolean({ chanceTrue: 0.6 })
        ? randPastDate().toISOString()
        : null
    },
    created() {
      return randPastDate().toISOString()
    },
    modified() {
      return randRecentDate().toISOString()
    },
    herd_size_multiplier() {
      return randChanceBoolean({ chanceTrue: 0.1 })
        ? randNumber({ min: 1, max: 10 })
        : null
    },
    display_price() {
      return randNumber({ min: 10, max: 150, precision: 2 })
    },
    hidden_items_display_price() {
      return randChanceBoolean({ chanceTrue: 0.2 })
        ? randNumber({ min: 5, max: 50, precision: 2 })
        : 0
    },
    is_minimum_charge_active() {
      return randChanceBoolean({ chanceTrue: 0.3 })
    },
    result() {
      return randChanceBoolean({ chanceTrue: 0.7 })
        ? randNumber({ min: 0, max: 200, precision: 2 }).toString()
        : null
    },
    result_entered() {
      return randChanceBoolean({ chanceTrue: 0.7 })
        ? randRecentDate().toISOString()
        : null
    },
    approved() {
      return randChanceBoolean({ chanceTrue: 0.5 })
    },
    approved_date() {
      return randChanceBoolean({ chanceTrue: 0.5 })
        ? randRecentDate().toISOString()
        : null
    },
    measurement_unit() {
      const units = [
        'g/dL',
        'U/L',
        'mg/dL',
        'mmol/L',
        'mEq/L',
        '%',
        'fL',
        'K/µL',
        'M/µL',
        'g/L',
      ]
      return rand(units)
    },
    device() {
      const devices = [
        'IDEXX Catalyst One',
        'IDEXX ProCyte Dx',
        'Abaxis VetScan VS2',
        'Heska Element DC',
        'Manual Count',
      ]
      return rand(devices)
    },
    remarks() {
      return randChanceBoolean({ chanceTrue: 0.3 })
        ? rand([
            'Hemolyzed sample',
            'Slightly lipemic',
            'Retest recommended',
            'Patient was non-fasted',
            'Sample clotted',
          ])
        : null
    },
    description() {
      return randChanceBoolean({ chanceTrue: 0.4 }) ? randProductName() : null
    },
    reference_low() {
      return randChanceBoolean({ chanceTrue: 0.8 })
        ? randNumber({ min: 0, max: 50, precision: 2 })
        : null
    },
    reference_high() {
      return randChanceBoolean({ chanceTrue: 0.8 })
        ? randNumber({ min: 50, max: 200, precision: 2 })
        : null
    },
    result_out_of_references() {
      return false
    },

    withCreatedUser: trait({
      afterCreate(analysis, server) {
        const createdUser = getExistingEntity(server, 'user')
        analysis.update({ createdUser })
      },
    }),

    withModifiedUser: trait({
      afterCreate(analysis, server) {
        const modifiedUser = getExistingEntity(server, 'user')
        analysis.update({ modifiedUser })
      },
    }),

    afterCreate(analysis, server) {
      if (!analysis.consultationLaboratoryReferral) {
        const referral = getExistingEntity(
          server,
          'consultationLaboratoryReferral',
        )
        analysis.update({ consultationLaboratoryReferral: referral })
      }
    },
  },
)

export function routes(server: MockServer) {
  server.get(
    '/consultation_laboratory_referrals/:parent_lookup_referral/analyses',
    function (this: MirageServer, schema, request) {
      const referralId = request.params.parent_lookup_referral
      const analyses = schema.where('consultationLaboratoryAnalysis', {
        consultationLaboratoryReferralId: referralId,
      })

      const filtered = filterable(analyses, request, {
        'name.in': (item, value) => {
          const names = value.split(',').map((v: string) => v.trim())
          return names.includes(item.attrs.name)
        },
        result_outside_references: (item, value) => {
          return item.attrs.result_out_of_references === (value === 'true')
        },
        search: (item, value) => {
          const searchTerm = value.toLowerCase()
          return (
            item.attrs.name?.toLowerCase().includes(searchTerm) ||
            item.attrs.code?.toLowerCase().includes(searchTerm)
          )
        },
      })

      const { data, meta } = pageable(filtered, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )
}

export function seeds(server: MockServer) {
  const referralsWithResults = server.schema.where(
    'consultationLaboratoryReferral',
    { any_row_has_results: true },
  ).models

  const referralsWithoutResults = server.schema.where(
    'consultationLaboratoryReferral',
    { any_row_has_results: false },
  ).models

  // Create analyses WITH results for referrals with anyRowHasResults: true
  referralsWithResults.forEach((referral) => {
    const analysisCount = randNumber({ min: 2, max: 6 })

    // Create analyses with results in range
    const inRangeCount = Math.floor(analysisCount * 0.7)
    server.createList(
      'consultationLaboratoryAnalysis',
      inRangeCount,
      'withCreatedUser',
      'withModifiedUser',
      {
        consultationLaboratoryReferral: referral,
        result: () => {
          const low = randNumber({ min: 10, max: 40, precision: 2 })
          const high = randNumber({ min: 60, max: 100, precision: 2 })
          return randNumber({
            min: low + 5,
            max: high - 5,
            precision: 2,
          }).toString()
        },
        reference_low: () => randNumber({ min: 10, max: 40, precision: 2 }),
        reference_high: () => randNumber({ min: 60, max: 100, precision: 2 }),
      },
    )

    // Create analyses with results out of range
    const outOfRangeCount = analysisCount - inRangeCount
    server.createList(
      'consultationLaboratoryAnalysis',
      outOfRangeCount,
      'withCreatedUser',
      'withModifiedUser',
      {
        consultationLaboratoryReferral: referral,
        result: () => {
          const isLow = randChanceBoolean({ chanceTrue: 0.5 })
          if (isLow) {
            return randNumber({ min: 0, max: 8, precision: 2 }).toString()
          }
          return randNumber({ min: 105, max: 200, precision: 2 }).toString()
        },
        reference_low: () => randNumber({ min: 10, max: 40, precision: 2 }),
        reference_high: () => randNumber({ min: 60, max: 100, precision: 2 }),
      },
    )
  })

  // Create analyses WITHOUT results for referrals with anyRowHasResults: false
  referralsWithoutResults.forEach((referral) => {
    server.create(
      'consultationLaboratoryAnalysis',
      'withCreatedUser',
      'withModifiedUser',
      {
        consultationLaboratoryReferral: referral,
        result: null,
        result_entered: null,
        result_out_of_references: false,
      },
    )
  })
}
